import { Injectable } from '@angular/core';
import { ApiCacheEntry } from './api-cache.models';

// Memória primeiro (leitura instantânea, sem custo de JSON.parse repetido) +
// sessionStorage como backup — só serve pra sobreviver a um F5 na mesma aba.
// Sem essa segunda camada, um reload zera tudo e volta a mostrar skeleton
// mesmo com o dado ainda "fresco" do ponto de vista do usuário.
@Injectable({
  providedIn: 'root'
})
export class ApiCacheService {
  private readonly prefix = 'ss_api_cache:v1:';
  private readonly memory = new Map<string, ApiCacheEntry<unknown>>();

  get<T>(key: string): ApiCacheEntry<T> | null {
    const cached = this.memory.get(key) as ApiCacheEntry<T> | undefined;
    if (cached) {
      return cached;
    }

    const raw = sessionStorage.getItem(this.storageKey(key));
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as ApiCacheEntry<T>;
      this.memory.set(key, parsed);
      return parsed;
    } catch {
      // Cache inválido/incompatível (ex: mudou o shape do DTO) — remove pra
      // não ficar tentando de novo em toda leitura.
      sessionStorage.removeItem(this.storageKey(key));
      return null;
    }
  }

  set<T>(key: string, data: T, etag: string | null): void {
    const entry: ApiCacheEntry<T> = {
      etag,
      data,
      storedAt: Date.now()
    };

    this.memory.set(key, entry);

    try {
      sessionStorage.setItem(this.storageKey(key), JSON.stringify(entry));
    } catch (error) {
      // QuotaExceededError, modo privado, storage bloqueado — a requisição
      // continua funcionando normalmente, só perde a persistência entre reloads.
      console.warn('[ApiCache] Não foi possível persistir em sessionStorage:', key, error);
    }
  }

  remove(key: string): void {
    this.memory.delete(key);
    sessionStorage.removeItem(this.storageKey(key));
  }

  /** Chamar no logout e ao trocar de loja/usuário/tenant ativo. */
  clearAll(): void {
    this.memory.clear();

    const keysToRemove: string[] = [];
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  private storageKey(key: string): string {
    return `${this.prefix}${key}`;
  }
}
